import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { CognitoUserPool } from "amazon-cognito-identity-js";
import Layout from "./components/layout/Layout";
import OrganizationsPage from "./pages/OrganizationsPage";
import TrainersPage from "./pages/TrainersPage";
import StudentsPage from "./pages/StudentsPage";
import LoginPage from "./pages/LoginPage";
import RolesPage from "./pages/RolesPage";
import ProtectedRoute from "./components/ProtectedRoute";
import { AppProvider } from "./contexts/AppContext";
import ExercisesPage from "./pages/ExercisesPage";
import OrganizationDetailsPage from "./pages/OrganizationDetailsPage";
import PermissionsPage from "./pages/PermissionPage";
import ApiProvider from "./components/ApiProvider";
import AuthService from "./services/AuthService";
import { SessionsPage } from "./pages/SessionsPage";
import { SessionDetailsPage } from "./pages/SessionDetailsPage";
import StudentDetailsPage from "./pages/StudentDetailsPage";
import { TrainerDetailsPage } from "./pages/TrainerDetailsPage";
import StudentSessionsPage from "./pages/StudentSessionsPage";
import SettingsPage from "./pages/SettingsPage";
import { ThemeProvider } from "./contexts/ThemeContext";
import AnalyticsScreen from "./pages/AnalyticsPage";
import FinanceScreen from "./pages/FinancePage";
import NotificationsPage from "./pages/NotificationsPage";
import { ToastProvider } from "./contexts/ToastContext";
import { ToastContainer } from "./components/ui/Toast";
import LoadingSpinner from "./components/ui/LoadingSpinner";
import ExerciseDetailsPage from "./pages/ExerciseDetailsPage";

// Cognito User Pool configuration
const userPool = new CognitoUserPool({
  UserPoolId: process.env.REACT_APP_USER_POOL_ID,
  ClientId: process.env.REACT_APP_CLIENT_ID,
});

// Create auth service instance
const authService = new AuthService(userPool);

const LoadingScreen = ({
  message = "Uygulama yükleniyor...",
  onLoadingComplete,
  delay = 2000,
}) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      if (onLoadingComplete) {
        onLoadingComplete();
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [delay, onLoadingComplete]);

  if (!isVisible) return null;

  return (
    <div className={`fixed inset-0 z-[999] flex flex-col items-center justify-center bg-white dark:bg-[#09090b] transition-all duration-700 ${isVisible ? 'opacity-100' : 'opacity-0 scale-105'}`}>
      <LoadingSpinner
        fullScreen={true}
        size="large"
        showLogo={true}
        message={message}
      />
      {/* Subtle branding footer */}
      <div className="absolute bottom-12 animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-500">
        <p className="text-[10px] uppercase tracking-[0.3em] font-black text-zinc-400 dark:text-zinc-600">
          Powered by Integrity Chain
        </p>
      </div>
    </div>
  );
};


// Custom auth context
const AuthContext = React.createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const hasChecked = useRef(false);

  // Check session status and refresh if needed
  const checkSessionStatus = useCallback(async () => {
    try {
      const idToken = localStorage.getItem("idToken");

      // If token is expired or close to expiration, refresh it
      if (idToken && authService.isTokenExpired(idToken)) {
        console.log("Token is expired or close to expiration, refreshing...");
        await authService.refreshSession();
      }
    } catch (error) {
      console.error("Session check failed:", error);
      logout();
    }
  }, []);

  // Set up a timer to periodically check token status
  useEffect(() => {
    if (isAuthenticated) {
      // Check session status every 5 minutes
      const sessionCheckInterval = setInterval(
        () => {
          checkSessionStatus();
        },
        5 * 60 * 1000,
      ); // 5 minutes

      return () => clearInterval(sessionCheckInterval);
    }
  }, [isAuthenticated, checkSessionStatus]);

  const checkCurrentUser = useCallback(async () => {
    const currentUser = userPool.getCurrentUser();

    if (currentUser) {
      try {
        const idToken = localStorage.getItem("idToken");
        if (idToken && authService.isTokenExpired(idToken)) {
          await authService.refreshSession();
        }

        currentUser.getSession((err, session) => {
          if (err) {
            console.error("Session error:", err);
            logout();
            return;
          }

          if (session && session.isValid()) {
            // Get user attributes
            currentUser.getUserAttributes(async (err, attributes) => {
              if (err) {
                console.error("Error getting user attributes:", err);
                logout();
                return;
              }

              const idToken = session.getIdToken();
              const payload = idToken.payload;
              const groups = payload["cognito:groups"] || [];

              let role = "student";
              if (groups.includes("SuperAdmins")) role = "super_admin";
              else if (groups.includes("OrganizationManager")) role = "manager";
              else if (groups.includes("Trainers")) role = "trainer";
              else if (groups.includes("Students")) role = "student";
              else if (groups.length > 0) {
                // Pick the first group that isn't one of the core ones if they exist
                role = groups[0].toLowerCase();
              } else {
                role = attributes.find((attr) => attr.Name === "custom:role")?.Value || "student";
              }

              let permissions = [];
              let pages = [];
              try {
                const { permissionsAPI } = await import("./services/api");
                const response = await permissionsAPI.getRolePermissions(role);
                const roleData = response.data?.data || response.data || {};
                permissions = roleData.permissions || [];
                pages = roleData.pages || [];
              } catch (permErr) {
                console.error("Failed to load user role data:", permErr);
              }

              const userInfo = {
                username: currentUser.getUsername(),
                email:
                  attributes.find((attr) => attr.Name === "email")?.Value || "",
                name:
                  attributes.find((attr) => attr.Name === "name")?.Value || "",
                given_name:
                  attributes.find((attr) => attr.Name === "given_name")
                    ?.Value || "",
                family_name:
                  attributes.find((attr) => attr.Name === "family_name")
                    ?.Value || "",
                role,
                permissions,
                pages,
                session: session,
              };

              setUser(userInfo);
              setIsAuthenticated(true);
              setLoading(false);
            });
          } else {
            logout();
          }
        });
      } catch (error) {
        console.error("Error checking current user:", error);
        logout();
      }
    } else {
      setLoading(false);
    }
  }, []);

  // Check if user is already logged in
  useEffect(() => {
    if (!hasChecked.current) {
      checkCurrentUser();
      hasChecked.current = true;
    }
  }, [checkCurrentUser]);

  const logout = () => {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.signOut();
    }

    // Clear localStorage
    localStorage.removeItem("accessToken");
    localStorage.removeItem("idToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("selectedOrganizationId");
    localStorage.removeItem("selectedOrganizationName");

    setUser(null);
    setIsAuthenticated(false);
    setLoading(false);
  };

  const refreshSession = async () => {
    try {
      return await authService.refreshSession();
    } catch (error) {
      console.error("Error refreshing session:", error);
      logout();
      throw error;
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    logout,
    checkCurrentUser,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const AuthenticatedApp = () => {
  return (
    <ApiProvider>
      <AppProvider>
        <Router>
          <Layout>
            <Routes>
              {/* Default redirect */}
              <Route path="/" element={<Navigate to="/sessions" replace />} />

              {/* Sessions - Available to all authenticated users */}
              <Route path="/sessions" element={<SessionsPage />} />
              <Route
                path="/sessions/:sessionId"
                element={<SessionDetailsPage />}
              />

              {/* Organizations */}
              <Route
                path="/organizations"
                element={
                  <ProtectedRoute requiredPage="/organizations">
                    <OrganizationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/roles"
                element={
                  <ProtectedRoute requiredPage="/roles">
                    <RolesPage />
                  </ProtectedRoute>
                }
              />

              {/* Organization Details */}
              <Route
                path="/organizations/:organizationId"
                element={
                  <ProtectedRoute requiredPage="/organizations">
                    <OrganizationDetailsPage />
                  </ProtectedRoute>
                }
              />

              {/* Trainers */}
              <Route
                path="/trainers"
                element={
                  <ProtectedRoute requiredPage="/trainers">
                    <TrainersPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/trainers/:trainerId"
                element={
                  <ProtectedRoute requiredPage="/trainers">
                    <TrainerDetailsPage />
                  </ProtectedRoute>
                }
              />

              {/* Students */}
              <Route
                path="/students"
                element={
                  <ProtectedRoute requiredPage="/students">
                    <StudentsPage />
                  </ProtectedRoute>
                }
              />

              {/* Settings is a personal page (theme, language, password) —
                  every logged-in user must reach it, regardless of role. */}
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/students/:studentId"
                element={
                  <ProtectedRoute requiredPage="/students">
                    <StudentDetailsPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/student-sessions/:studentId/:subscriptionId"
                element={
                  <ProtectedRoute requiredPage="/sessions">
                    <StudentSessionsPage />
                  </ProtectedRoute>
                }
              />

              {/* Exercises */}
              <Route
                path="/exercises"
                element={
                  <ProtectedRoute requiredPage="/exercises">
                    <ExercisesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/exercises/:exerciseId"
                element={
                  <ProtectedRoute requiredPage="/exercises">
                    <ExerciseDetailsPage />
                  </ProtectedRoute>
                }
              />

              {/* Finance */}
              <Route
                path="/finance"
                element={
                  <ProtectedRoute requiredPage="/finance">
                    <FinanceScreen />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/analytics"
                element={
                  <ProtectedRoute requiredPage="/analytics">
                    <AnalyticsScreen />
                  </ProtectedRoute>
                }
              />

              {/* Notifications */}
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute requiredPage="/notifications">
                    <NotificationsPage />
                  </ProtectedRoute>
                }
              />

              {/* Permissions */}
              <Route
                path="/permissions"
                element={
                  <ProtectedRoute requiredPage="/permissions">
                    <PermissionsPage />
                  </ProtectedRoute>
                }
              />

              {/* GYM Account - Super admin only */}
              <Route
                path="/gym-account"
                element={
                  <ProtectedRoute requiredRoles={["super_admin"]}>
                    <div className="p-6">
                      <h2 className="text-2xl font-bold">
                        GYM Account Page - Coming Soon
                      </h2>
                    </div>
                  </ProtectedRoute>
                }
              />

              {/* Catch all - redirect to sessions */}
              <Route path="*" element={<Navigate to="/sessions" replace />} />
            </Routes>
          </Layout>
        </Router>
      </AppProvider>
    </ApiProvider>
  );
};
const AppContent = () => {
  const { isAuthenticated, loading } = useAuth();

  // Show loading screen while checking authentication
  if (loading) {
    return <LoadingScreen />;
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Show main authenticated app
  return <AuthenticatedApp />;
};

// Main App component
function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <AppContent />
          <ToastContainer />
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
